import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { FolderOpen } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { importApi } from '@/api/endpoints/import';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { ImportReport, ImportedSchool } from '@/types';
import { formatDateTime } from '@/utils/format';
import { cn } from '@/utils/cn';

interface JsonPreview {
  file: File;
  entryCount: number;
  schools: string[];
}

const parseJsonPreview = (file: File): Promise<JsonPreview> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target?.result as string);
        if (!Array.isArray(raw)) {
          reject(new Error('Plik JSON musi zawierać tablicę wpisów.'));
          return;
        }
        const schools = [
          ...new Set(
            raw
              .map((entry: { response?: { input_text_1?: string } }) => entry?.response?.input_text_1?.trim())
              .filter(Boolean) as string[],
          ),
        ];
        resolve({ file, entryCount: raw.length, schools });
      } catch {
        reject(new Error('Nie można odczytać pliku JSON. Sprawdź poprawność formatu.'));
      }
    };
    reader.onerror = () => reject(new Error('Błąd odczytu pliku.'));
    reader.readAsText(file, 'utf-8');
  });
};

export const ImportPage = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<JsonPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['import-history'],
    queryFn: importApi.getHistory,
  });

  const { data: stats } = useQuery({
    queryKey: ['import-stats'],
    queryFn: importApi.getStats,
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => importApi.uploadJson(file),
    onSuccess: (data) => {
      setReport(data);
      setPreview(null);
      toast.success(`Import zakończony: ${data.imported} zaimportowanych`);
      queryClient.invalidateQueries({ queryKey: ['import-history'] });
      queryClient.invalidateQueries({ queryKey: ['import-stats'] });
      queryClient.invalidateQueries({ queryKey: ['schools'] });
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    },
    onError: (err: Error) => {
      toast.error(`Błąd importu: ${err.message}`);
    },
  });

  const handleFile = async (file: File) => {
    setPreviewError(null);
    setReport(null);
    if (!file.name.endsWith('.json')) {
      setPreviewError('Akceptowane są tylko pliki .json');
      return;
    }
    try {
      const p = await parseJsonPreview(file);
      setPreview(p);
    } catch (err) {
      setPreviewError((err as Error).message);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  const handleImport = () => {
    if (preview) importMutation.mutate(preview.file);
  };

  const getSchoolBadgeColor = (school: ImportedSchool) =>
    school.isNew ? 'success' : 'info';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Import JSON</h2>
        <p className="mt-1 text-sm text-gray-500">
          Importuj zgłoszenia z formularza WordPress (FluentForms) w formacie JSON
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.schools}</p>
              <p className="text-xs text-gray-500">Szkoły w bazie</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.participants}</p>
              <p className="text-xs text-gray-500">Uczestnicy w bazie</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.imports}</p>
              <p className="text-xs text-gray-500">Zaimportowane wpisy</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Wgraj plik JSON</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className={cn(
              'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors cursor-pointer',
              isDragging
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50',
            )}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FolderOpen className="h-10 w-10 mb-3 text-competition-accent" />
            <p className="text-sm font-medium text-gray-700">
              Przeciągnij i upuść plik JSON tutaj
            </p>
            <p className="mt-1 text-xs text-gray-500">lub kliknij, aby wybrać plik</p>
            <p className="mt-2 text-xs text-gray-400">Tylko pliki .json, max 10 MB</p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleInputChange}
          />

          {previewError && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-700">{previewError}</p>
            </div>
          )}

          {preview && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-900">{preview.file.name}</p>
                  <p className="text-xs text-blue-700 mt-1">
                    {preview.entryCount} wpisów · {preview.schools.length} unikalnych szkół
                  </p>
                </div>
                <Button
                  onClick={() => setPreview(null)}
                  variant="ghost"
                  size="sm"
                  className="text-blue-600"
                >
                  Usuń
                </Button>
              </div>
              {preview.schools.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-blue-800 mb-1">Szkoły w pliku:</p>
                  <div className="flex flex-wrap gap-1">
                    {preview.schools.map((s) => (
                      <span key={s} className="text-xs bg-blue-100 text-blue-800 rounded px-2 py-0.5">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Button
                onClick={handleImport}
                
                className="w-full"
              >
                Importuj {preview.entryCount} wpisów
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {report && (
        <Card>
          <CardHeader>
            <CardTitle>Raport importu</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-md bg-green-50 p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{report.imported}</p>
                <p className="text-xs text-green-600">Zaimportowane</p>
              </div>
              <div className="rounded-md bg-yellow-50 p-3 text-center">
                <p className="text-2xl font-bold text-yellow-700">{report.skipped}</p>
                <p className="text-xs text-yellow-600">Pominięte (duplikaty)</p>
              </div>
              <div className="rounded-md bg-red-50 p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{report.errors.length}</p>
                <p className="text-xs text-red-600">Błędy</p>
              </div>
            </div>

            {report.schools.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Przetworzone szkoły:</p>
                <div className="flex flex-wrap gap-2">
                  {report.schools.map((school, i) => (
                    <div key={i} className="flex items-center gap-1.5 rounded-md bg-gray-50 border px-3 py-1.5">
                      <Badge variant={getSchoolBadgeColor(school)}>
                        {school.isNew ? 'Nowa' : 'Zaktualizowana'}
                      </Badge>
                      <span className="text-sm text-gray-800">{school.name}</span>
                      <span className="text-xs text-gray-500">({school.participantsCount} uczestników)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.errors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">Błędy:</p>
                <div className="space-y-1">
                  {report.errors.map((err, i) => (
                    <div key={i} className="rounded bg-red-50 px-3 py-2 text-xs text-red-700">
                      <span className="font-medium">Wpis #{err.entryId} (seria {err.serialNumber}):</span>{' '}
                      {err.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Historia importów</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {historyLoading ? (
            <LoadingSpinner size="md" className="py-8" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID wpisu</TableHead>
                  <TableHead>Nr seryjny</TableHead>
                  <TableHead>Szkoła</TableHead>
                  <TableHead>Zaimportowano</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!history || history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-gray-500">
                      Brak historii importów
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="font-mono text-xs">{h.formEntryId}</TableCell>
                      <TableCell>{h.serialNumber}</TableCell>
                      <TableCell>{h.schoolName}</TableCell>
                      <TableCell>{formatDateTime(h.importedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
